<template>
    <div class="page-container">
        <div class="settings-form">
            <!-- 页面标题和统计 -->
            <div class="header-section">
                <h2 class="page-title">客户人群管理</h2>
                <div class="stats-section">
                    <el-row :gutter="20">
                        <el-col :span="6">
                            <div class="stat-card">
                                <div class="stat-icon total">
                                    <el-icon>
                                        <Document />
                                    </el-icon>
                                </div>
                                <div class="stat-content">
                                    <div class="stat-number">{{ stats.total_audiences || 0 }}</div>
                                    <div class="stat-label">总人群数</div>
                                </div>
                            </div>
                        </el-col>
                        <el-col :span="6">
                            <div class="stat-card">
                                <div class="stat-icon active">
                                    <el-icon>
                                        <CircleCheck />
                                    </el-icon>
                                </div>
                                <div class="stat-content">
                                    <div class="stat-number">{{ statusCount.active || 0 }}</div>
                                    <div class="stat-label">活跃人群</div>
                                </div>
                            </div>
                        </el-col>
                        <el-col :span="6">
                            <div class="stat-card">
                                <div class="stat-icon pending">
                                    <el-icon>
                                        <Clock />
                                    </el-icon>
                                </div>
                                <div class="stat-content">
                                    <div class="stat-number">{{ statusCount.pending || 0 }}</div>
                                    <div class="stat-label">处理中</div>
                                </div>
                            </div>
                        </el-col>
                        <el-col :span="6">
                            <div class="stat-card">
                                <div class="stat-icon files">
                                    <el-icon>
                                        <Files />
                                    </el-icon>
                                </div>
                                <div class="stat-content">
                                    <div class="stat-number">{{ fileStats.total_files || 0 }}</div>
                                    <div class="stat-label">数据文件</div>
                                </div>
                            </div>
                        </el-col>
                    </el-row>
                </div>
            </div>

            <!-- 操作按钮区域 -->
            <div class="action-bar">
                <!-- 修改创建按钮 -->
                <el-button type="primary" size="large" @click="handleCreateClick">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    创建客户人群
                </el-button>
                <el-button type="success" size="large" @click="syncAudiences" :loading="syncLoading">
                    <el-icon>
                        <Refresh />
                    </el-icon>
                    同步人群数据
                </el-button>
                <el-button type="info" size="large" @click="handleShowFilesDialog">
                    <el-icon>
                        <Files />
                    </el-icon>
                    查看数据文件
                </el-button>

                <div class="sync-status" v-if="syncStatus.last_sync">
                    <div class="status-item">
                        <span class="label">最后同步：</span>
                        <span class="value">{{ formatTime(syncStatus.last_sync.sync_time) }}</span>
                    </div>
                    <div class="status-item">
                        <span class="label">同步结果：</span>
                        <span class="value success" v-if="syncStatus.last_sync.success_count > 0">
                            成功 {{ syncStatus.last_sync.success_count }} 条
                        </span>
                        <span class="value fail" v-if="syncStatus.last_sync.fail_count > 0">
                            失败 {{ syncStatus.last_sync.fail_count }} 条
                        </span>
                    </div>
                </div>
            </div>

            <!-- 搜索区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.keyword" placeholder="搜索人群名称、广告主" clearable size="large"
                    style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <el-select v-model="searchForm.status" placeholder="状态筛选" clearable size="large"
                    style="width: 150px; margin-right: 15px">
                    <el-option label="全部状态" value=""></el-option>
                    <el-option label="活跃" value="SUCCESS"></el-option>
                    <el-option label="处理中" value="PROCESSING"></el-option>
                    <el-option label="等待中" value="PENDING"></el-option> <!-- 添加这一行 -->
                    <el-option label="已删除" value="DELETED"></el-option>
                </el-select>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>
                    搜索
                </el-button>

                <el-button type="info" size="large" @click="resetSearch">
                    <el-icon>
                        <RefreshLeft />
                    </el-icon>
                    重置
                </el-button>
            </div>

            <!-- 客户人群列表 -->
            <el-table :data="audienceList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
                <el-table-column prop="audience_id" label="人群ID" width="120" />
                <el-table-column prop="name" label="人群名称" min-width="200" show-overflow-tooltip />
                <el-table-column prop="corporation_name" label="广告主" width="150" show-overflow-tooltip />
                <el-table-column prop="auth_name" label="授权账户" width="120" />

                <el-table-column label="人群类型" width="120">
                    <template #default="{ row }">
                        <el-tag :type="getTypeTagType(row.type)" size="small">
                            {{ getTypeText(row.type) }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="状态" width="100">
                    <template #default="{ row }">
                        <el-tag :type="getStatusTagType(row.status)" size="small">
                            {{ getStatusText(row.status) }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="用户数量" width="100">
                    <template #default="{ row }">
                        <span v-if="row.user_count > 0">{{ row.user_count }} 人</span>
                        <span v-else class="empty-value">暂无</span>
                    </template>
                </el-table-column>

                <el-table-column prop="description" label="描述" width="200" show-overflow-tooltip>
                    <template #default="{ row }">
                        <span v-if="row.description">{{ row.description }}</span>
                        <span v-else class="empty-value">无描述</span>
                    </template>
                </el-table-column>

                <el-table-column prop="created_time" label="创建时间" width="180">
                    <template #default="{ row }">
                        {{ formatTime(row.created_time) }}
                    </template>
                </el-table-column>

                <el-table-column prop="last_sync_time" label="最后同步" width="180">
                    <template #default="{ row }">
                        {{ formatTime(row.last_sync_time) }}
                    </template>
                </el-table-column>

                <el-table-column label="操作" width="380" fixed="right">
                    <template #default="scope">
                        <div class="action-buttons">
                            <el-button type="primary" size="small" @click="editAudience(scope.row)">
                                <el-icon>
                                    <Edit />
                                </el-icon>
                                编辑
                            </el-button>
                            <el-button type="success" size="small" @click="openUploadDialog(scope.row)">
                                <el-icon>
                                    <Upload />
                                </el-icon>
                                上传数据
                            </el-button>
                            <el-button type="warning" size="small" @click="viewFiles(scope.row)">
                                <el-icon>
                                    <View />
                                </el-icon>
                                查看文件
                            </el-button>
                            <el-button type="danger" size="small" @click="deleteAudience(scope.row)"
                                :loading="deleteLoading[scope.row.id]">
                                <el-icon>
                                    <Delete />
                                </el-icon>
                                删除
                            </el-button>
                        </div>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 50, 100]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchAudienceList"
                    @current-change="fetchAudienceList" />
            </div>
        </div>

        <!-- 创建/编辑客户人群弹窗 -->
        <el-dialog :title="editMode ? '编辑客户人群' : '创建客户人群'" v-model="showCreateDialog" width="700px"
            :close-on-click-modal="false" @close="handleDialogClose">
            <el-form :model="audienceForm" :rules="audienceRules" ref="audienceFormRef" label-width="120px">
                <el-form-item label="人群名称" prop="name" required>
                    <el-input v-model="audienceForm.name" placeholder="请输入人群名称" @input="handleNameChange" />
                </el-form-item>

                <el-form-item label="广告账户" prop="auth_id" required>
                    <el-select v-model="audienceForm.auth_id" placeholder="请选择广告账户" style="width: 100%"
                        @change="handleAuthChange" clearable>
                        <el-option v-for="auth in authList" :key="auth.id" :label="auth.ghz_name" :value="auth.id" />
                    </el-select>
                </el-form-item>

                <el-form-item label="广告商" prop="account_id" required>
                    <el-select v-model="audienceForm.account_id" placeholder="请先选择广告账户" style="width: 100%"
                        :disabled="!audienceForm.auth_id" clearable>
                        <el-option v-for="account in accountList" :key="account.account_id"
                            :label="`${account.corporation_name} (${account.account_id})`"
                            :value="account.account_id" />
                    </el-select>
                </el-form-item>

                <el-form-item label="人群类型" prop="type" required>
                    <el-select v-model="audienceForm.type" placeholder="请选择人群类型" style="width: 100%">
                        <el-option label="客户文件人群" value="CUSTOMER_FILE" />
                    </el-select>
                </el-form-item>

                <!-- 替换原来的外部人群ID输入框 -->
                <el-form-item label="外部人群ID">
                    <div class="outer-id-container">
                        <el-input v-model="audienceForm.outer_audience_id" placeholder="自动生成外部人群ID"
                            :disabled="autoGenerateOuterId">
                            <template #append>
                                <el-tooltip content="自动生成符合要求的外部人群ID">
                                    <el-checkbox v-model="autoGenerateOuterId" style="margin-right: 5px;"
                                        @change="handleAutoGenerateChange">
                                        自动生成
                                    </el-checkbox>
                                </el-tooltip>
                            </template>
                        </el-input>
                        <el-button v-if="!autoGenerateOuterId" type="primary" link @click="generateNewOuterId"
                            class="regenerate-btn">
                            <el-icon>
                                <Refresh />
                            </el-icon>
                            重新生成
                        </el-button>
                    </div>
                    <div class="form-tip">
                        广告主对人群在自己系统里的编码，只能包含数字、字母或下划线，长度1-128字节
                    </div>
                </el-form-item>

                <el-form-item label="人群描述">
                    <el-input v-model="audienceForm.description" type="textarea" :rows="3"
                        placeholder="请输入人群描述信息（可选）" />
                </el-form-item>
                <!-- 只在编辑模式下显示深度数据合作开关 -->
                <el-form-item label="深度数据合作" v-if="editMode">
                    <el-switch v-model="audienceForm.cooperated" active-text="是" inactive-text="否" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="handleDialogClose">取消</el-button>
                <el-button type="primary" @click="submitAudience" :loading="submitting">
                    {{ editMode ? '更新人群' : '创建人群' }}
                </el-button>
            </template>
        </el-dialog>

        <!-- 上传数据文件弹窗 -->
        <el-dialog title="上传人群数据文件" v-model="showUploadDialog" width="600px" :close-on-click-modal="false">
            <el-form :model="uploadForm" :rules="uploadRules" ref="uploadFormRef" label-width="120px">
                <el-form-item label="人群信息">
                    <el-input v-model="currentAudience.name" disabled />
                </el-form-item>

                <el-form-item label="用户ID类型" prop="user_id_type" required>
                    <el-select v-model="uploadForm.user_id_type" placeholder="请选择用户ID类型" style="width: 100%"
                        @change="handleUserIdTypeChange">
                        <el-option label="手机号" value="MOBILE_PHONE" />
                        <el-option label="微信OpenId" value="WECHAT_OPENID" />
                    </el-select>
                </el-form-item>

                <!-- 文件上传区域保持不变 -->
                <el-form-item label="数据文件" prop="file_url" required>
                    <div class="file-upload-section">
                        <!-- 文件上传区域 -->
                        <div class="file-upload-area" v-if="!uploadForm.file_url">
                            <el-upload class="file-uploader" :auto-upload="false" :show-file-list="false"
                                :on-change="handleFileChange" accept=".csv,.txt" :before-upload="beforeFileUpload">
                                <div class="upload-content">
                                    <el-icon size="48" color="#409EFF">
                                        <Upload />
                                    </el-icon>
                                    <div class="upload-text">
                                        <p class="upload-title">点击上传数据文件</p>
                                        <p class="upload-tip">支持 CSV、TXT 格式，每行一个用户ID</p>
                                    </div>
                                </div>
                            </el-upload>
                        </div>

                        <!-- 文件预览区域 -->
                        <div class="file-preview" v-else>
                            <div class="file-info">
                                <el-icon size="24" color="#67C23A">
                                    <Document />
                                </el-icon>
                                <div class="file-details">
                                    <p class="file-name">{{ uploadedFileName }}</p>
                                    <p class="file-size">{{ uploadedFileSize }}</p>
                                </div>
                                <div class="file-actions">
                                    <el-button type="danger" size="small" @click="removeFile">
                                        <el-icon>
                                            <Delete />
                                        </el-icon>
                                        删除
                                    </el-button>
                                </div>
                            </div>
                        </div>

                        <!-- 添加提示信息 -->
                        <div class="upload-tips">
                            <p class="tip-text">
                                请将号码写在*.csv(UTF-8编码)格式的文档中，一行一个号码，
                                <a href="https://jzguai01.oss-cn-shanghai.aliyuncs.com/uploads/file/20251011/20251011020343fd8d11244.csv"
                                    target="_blank" class="template-link">模版下载</a>
                            </p>
                            <p class="tip-note">(手机号建议上传1万以上)</p>
                        </div>
                    </div>
                </el-form-item>

                <el-form-item label="操作类型">
                    <el-select v-model="uploadForm.operation_type" placeholder="请选择操作类型" style="width: 100%">
                        <el-option label="追加" value="APPEND" />
                    </el-select>
                </el-form-item>

                <!-- 修改微信AppID表单项 -->
                <el-form-item label="微信AppID" :prop="uploadForm.user_id_type === 'WECHAT_OPENID' ? 'open_app_id' : ''"
                    :rules="uploadForm.user_id_type === 'WECHAT_OPENID' ? uploadRules.open_app_id : []"
                    :required="uploadForm.user_id_type === 'WECHAT_OPENID'">
                    <el-input v-model="uploadForm.open_app_id" placeholder="请输入微信AppID"
                        :class="{ 'required-field': uploadForm.user_id_type === 'WECHAT_OPENID' }" />
                    <div class="form-tip" v-if="uploadForm.user_id_type === 'WECHAT_OPENID'">
                        选择微信OpenId类型时，微信AppID为必填项
                    </div>
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="showUploadDialog = false">取消</el-button>
                <el-button type="primary" @click="submitUpload" :loading="uploading">
                    上传文件
                </el-button>
            </template>
        </el-dialog>

        <!-- 在文件列表弹窗后面添加这个同步账户选择弹窗 -->
        <el-dialog title="选择同步账户" v-model="showSyncSelectionDialog" width="500px" :close-on-click-modal="false">
            <div class="sync-selection-content">
                <p style="margin-bottom: 20px; color: #606266;">请选择要同步人群数据的账户：</p>

                <el-form :model="syncSelectionForm" label-width="80px">
                    <el-form-item label="同步账户" required>
                        <el-select v-model="syncSelectionForm.selectedAccount" placeholder="请选择账户" style="width: 100%">
                            <el-option v-for="option in syncAccountOptions" :key="option.label" :label="option.label"
                                :value="option" />
                        </el-select>
                    </el-form-item>

                    <!-- 显示选中账户的详细信息 -->
                    <el-card v-if="syncSelectionForm.selectedAccount" shadow="never" style="margin-top: 15px;">
                        <div style="padding: 10px;">
                            <p><strong>授权账户：</strong>{{ syncSelectionForm.selectedAccount.value.auth_name }}</p>
                            <p><strong>广告主：</strong>{{ syncSelectionForm.selectedAccount.value.corporation_name }}</p>
                            <p><strong>账户ID：</strong>{{ syncSelectionForm.selectedAccount.value.account_id }}</p>
                        </div>
                    </el-card>
                </el-form>
            </div>

            <template #footer>
                <el-button @click="showSyncSelectionDialog = false">取消</el-button>
                <el-button type="primary" @click="confirmSyncSelection" :loading="syncLoading">
                    开始同步
                </el-button>
            </template>
        </el-dialog>

        <!-- 数据文件列表弹窗 -->
        <!-- 数据文件列表弹窗 -->
        <el-dialog title="人群数据文件" v-model="showFilesDialog" width="1000px" top="5vh">
            <div class="files-content">
                <!-- 在文件列表弹窗的筛选区域添加查看全部按钮 -->
                <!-- 在文件列表弹窗的筛选区域添加同步按钮 -->
                <div class="files-filter">
                    <el-form :inline="true" :model="filesFilter">
                        <el-form-item label="人群">
                            <el-select v-model="filesFilter.audience_id" placeholder="选择人群" clearable
                                style="width: 200px" @change="handleFilesFilterChange">
                                <el-option label="全部人群" value=""></el-option>
                                <el-option v-for="audience in audienceList" :key="audience.audience_id"
                                    :label="audience.name" :value="audience.audience_id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" @click="fetchFileList">搜索</el-button>
                            <el-button @click="resetFilesFilter">重置</el-button>
                            <!-- 添加查看全部按钮 -->
                            <el-button type="info" @click="showAllFiles">查看全部</el-button>
                            <!-- 添加全局同步按钮 -->
                            <el-button type="success" @click="syncAllFilesStatus" :loading="filesLoading">
                                <el-icon>
                                    <Refresh />
                                </el-icon>
                                同步全部状态
                            </el-button>
                        </el-form-item>
                    </el-form>
                </div>
                <el-table :data="fileList" v-loading="filesLoading" height="400">
                    <el-table-column prop="custom_audience_file_id" label="文件ID" width="120" />
                    <el-table-column prop="audience_name" label="人群名称" width="200" show-overflow-tooltip />
                    <el-table-column prop="name" label="文件名" width="200" show-overflow-tooltip />
                    <el-table-column label="用户ID类型" width="120">
                        <template #default="{ row }">
                            <el-tag size="small">{{ getUserTypeText(row.user_id_type) }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="操作类型" width="100">
                        <template #default="{ row }">
                            <el-tag :type="row.operation_type === 'APPEND' ? 'success' : 'warning'" size="small">
                                {{ row.operation_type === 'APPEND' ? '追加' : '覆盖' }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="处理状态" width="120">
                        <template #default="{ row }">
                            <el-tag :type="getProcessStatusType(row.process_status)" size="small">
                                {{ getProcessStatusText(row.process_status) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="文件信息" width="150">
                        <template #default="{ row }">
                            <div>行数: {{ row.line_count || 0 }}</div>
                            <div>用户: {{ row.user_count || 0 }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="created_time" label="上传时间" width="180">
                        <template #default="{ row }">
                            {{ formatTime(row.created_time) }}
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" width="180">
                        <template #default="scope">
                            <div class="file-actions">
                                <el-button type="primary" size="small" @click="syncSingleFile(scope.row)">
                                    <el-icon>
                                        <Refresh />
                                    </el-icon>
                                    同步状态
                                </el-button>
                                <el-button type="danger" size="small" @click="deleteLocalFile(scope.row)"
                                    :loading="deleteFileLoading[scope.row.id]">
                                    删除
                                </el-button>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="pagination-container">
                    <el-pagination v-model:current-page="filesPagination.page"
                        v-model:page-size="filesPagination.pageSize" :total="filesPagination.total"
                        :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next" @size-change="fetchFileList"
                        @current-change="fetchFileList" />
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Search, Delete, Refresh, Document, View,
    CircleCheck, Clock, RefreshLeft, Plus,
    Edit, Upload, Files
} from '@element-plus/icons-vue'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';


export default {
    name: 'CustomAudienceManagement',
    components: {
        Search, Delete, Refresh, Document, View,
        CircleCheck, Clock, RefreshLeft, Plus,
        Edit, Upload, Files
    },
    data() {
        return {
            loading: false,
            syncLoading: false,
            submitting: false,
            uploading: false,
            filesLoading: false,
            deleteLoading: {},
            deleteFileLoading: {},
            showSyncSelectionDialog: false,
            syncAccountOptions: [],
            syncSelectionForm: {
                selectedAccount: null
            },
            autoGenerateOuterId: true, // 是否自动生成外部ID

            // 人群列表数据
            audienceList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 统计信息
            stats: {},
            syncStatus: {},
            statusCount: {},
            fileStats: {},

            // 搜索表单
            searchForm: {
                keyword: '',
                status: ''
            },

            // 弹窗控制
            showCreateDialog: false,
            showUploadDialog: false,
            showFilesDialog: false,
            editMode: false,

            audienceForm: {
                name: '',
                auth_id: '',
                account_id: '',
                type: 'CUSTOMER_FILE',
                outer_audience_id: '',
                description: '',
                cooperated: false,
                audience_id: ''
            },


            uploadedFileName: '',
            uploadedFileSize: '',
            uploadForm: {
                user_id_type: 'MOBILE_PHONE',
                file_url: '',
                operation_type: 'APPEND',
                open_app_id: ''
            },

            // 选项数据
            authList: [],
            accountList: [],

            // 当前操作的人群
            currentAudience: {},

            // 文件列表相关
            fileList: [],
            filesFilter: {
                audience_id: ''
            },
            filesPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 表单验证规则
            audienceRules: {
                name: [
                    { required: true, message: '请输入人群名称', trigger: 'blur' }
                ],
                auth_id: [
                    { required: true, message: '请选择广告账户', trigger: 'change' }
                ],
                account_id: [
                    { required: true, message: '请选择广告商', trigger: 'change' }
                ],
                type: [
                    { required: true, message: '请选择人群类型', trigger: 'change' }
                ]
            },

            uploadRules: {
                user_id_type: [
                    { required: true, message: '请选择用户ID类型', trigger: 'change' }
                ],
                file_url: [
                    { required: true, message: '请输入文件URL', trigger: 'blur' }
                ],
                open_app_id: [
                    {
                        required: true,
                        message: '微信AppID为必填项',
                        trigger: 'blur'
                    },
                    {
                        validator: (rule, value, callback) => {
                            if (!value || value.trim() === '') {
                                callback(new Error('请输入微信AppID'));
                            } else if (value.length < 1) {
                                callback(new Error('微信AppID不能为空'));
                            } else {
                                callback();
                            }
                        },
                        trigger: 'blur'
                    }
                ]
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('pc_user_info') || '{}')
        }
    },
    mounted() {
        this.fetchAudienceList();
        this.fetchSyncStatus();
        this.fetchStats();
        this.fetchAuthList();
    },
    methods: {

        // 处理用户ID类型变化
        handleUserIdTypeChange(value) {
            // 当切换用户ID类型时，重置微信AppID的验证状态
            if (value !== 'WECHAT_OPENID') {
                this.uploadForm.open_app_id = '';
            }

            // 清除之前的验证结果
            this.$nextTick(() => {
                this.$refs.uploadFormRef?.clearValidate(['open_app_id']);
            });
        },

        // 处理文件筛选变化
        handleFilesFilterChange(audienceId) {
            if (audienceId) {
                // 如果选择了特定人群，找到该人群信息
                const audience = this.audienceList.find(a => a.audience_id == audienceId);
                if (audience) {
                    this.currentAudience = audience;
                }
            } else {
                this.currentAudience = {};
            }
            this.fetchFileList();
        },

        // 同步全部文件状态
        async syncAllFilesStatus() {
            try {
                this.$message.info('正在同步全部文件状态...');

                // 如果有筛选特定人群，只同步该人群的文件
                if (this.filesFilter.audience_id && this.currentAudience.audience_id) {
                    await this.syncFileStatus(this.currentAudience);
                } else {
                    // 否则同步所有人群的文件
                    await this.syncAllAudiencesFiles();
                }

            } catch (error) {
                this.$message.error('同步文件状态失败');
                console.error('同步文件状态错误:', error);
            }
        },

        // 同步所有人群的文件状态
        async syncAllAudiencesFiles() {
            try {
                let successCount = 0;
                let failCount = 0;

                // 遍历所有人群进行同步
                for (const audience of this.audienceList) {
                    try {
                        await this.syncFileStatus(audience);
                        successCount++;
                    } catch (error) {
                        failCount++;
                        console.error(`同步人群 ${audience.name} 的文件状态失败:`, error);
                    }
                }

                if (failCount === 0) {
                    this.$message.success(`成功同步 ${successCount} 个人群的文件状态`);
                } else {
                    this.$message.warning(`同步完成: 成功 ${successCount} 个, 失败 ${failCount} 个`);
                }

            } catch (error) {
                this.$message.error('同步全部文件状态失败');
                console.error('同步全部文件状态错误:', error);
            }
        },
        // 处理弹窗关闭
        handleDialogClose() {
            this.showCreateDialog = false;
            // 延迟重置表单，避免在关闭动画期间重置导致闪烁
            setTimeout(() => {
                this.resetAudienceForm();
            }, 300);
        },
        // 处理创建按钮点击
        handleCreateClick() {
            this.resetAudienceForm(); // 确保重置表单
            this.showCreateDialog = true;
        },
        // 生成符合要求的外部人群ID
        generateOuterAudienceId() {
            const timestamp = Date.now().toString();
            const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
            const prefix = 'AUD';
            return `${prefix}_${timestamp}_${randomStr}`;
        },

        // 修改这个方法
        async handleShowFilesDialog() {
            this.filesFilter.audience_id = '';
            this.filesPagination.page = 1;
            this.showFilesDialog = true;

            // 重置 currentAudience，因为查看全部文件时不需要特定人群
            this.currentAudience = {};

            this.fetchFileList();
        },
        // 同步单个文件的详细状态
        async syncSingleFile(file) {
            try {
                // 先找到对应的人群信息
                const audience = this.audienceList.find(a => a.audience_id === file.audience_id);
                if (!audience) {
                    this.$message.error('未找到对应的人群信息');
                    return;
                }

                await this.syncFileStatus(audience);
            } catch (error) {
                this.$message.error('同步文件状态失败');
                console.error('同步文件状态错误:', error);
            }
        },
        // 在 methods 中添加
        // 在 methods 中添加
        async syncFileStatus(audience) {
            try {
                this.$message.info(`正在同步人群 "${audience.name}" 的文件状态...`);

                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'sync_file_status',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                        auth_id: audience.auth_id,
                        account_id: audience.account_id,
                        audience_id: audience.audience_id
                    }
                );

                if (response.data.success) {
                    this.$message.success(`人群 "${audience.name}" 文件状态同步成功`);
                    this.fetchFileList(); // 重新获取文件列表
                    return true;
                } else {
                    this.$message.error(response.data.error || '同步失败');
                    return false;
                }
            } catch (error) {
                this.$message.error(`同步人群 "${audience.name}" 文件状态失败`);
                console.error('同步文件状态错误:', error);
                throw error;
            }
        },

        // 监听人群名称变化，自动生成外部ID
        handleNameChange() {
            if (this.autoGenerateOuterId && this.audienceForm.name) {
                // 基于人群名称生成基础ID
                const baseName = this.audienceForm.name.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
                const timestamp = new Date().getTime().toString().slice(-6);
                const randomStr = Math.random().toString(36).substring(2, 6).toUpperCase();

                let outerId = `${baseName}_${timestamp}_${randomStr}`;

                // 确保长度不超过128字节
                if (outerId.length > 128) {
                    const maxBaseLength = 128 - timestamp.length - randomStr.length - 2; // -2 是为了下划线
                    outerId = `${baseName.substring(0, maxBaseLength)}_${timestamp}_${randomStr}`;
                }

                this.audienceForm.outer_audience_id = outerId;
            }
        },
        // 获取人群列表
        async fetchAudienceList() {
            this.loading = true;
            try {
                const params = {
                    action: this.searchForm.keyword ? 'search_audiences' : 'list_audiences',
                    store_id: this.userInfo.store_id,
                    user_id: this.userInfo.id,  // 添加user_id
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                };

                if (this.searchForm.keyword) {
                    params.keyword = this.searchForm.keyword;
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    params
                );

                if (data.data) {
                    this.audienceList = data.data;
                    this.pagination.total = data.pagination?.total || data.data.length;
                    this.calculateStatusCount();
                }
            } catch (error) {
                this.$message.error('获取人群列表失败');
                console.error('获取人群列表错误:', error);
            } finally {
                this.loading = false;
            }
        },

        // 处理文件选择变化
        async handleFileChange(file) {
            try {
                this.$message.info('文件上传中，请稍候...');
                const url = await this.uploadFile(file.raw);
                this.uploadForm.file_url = url;
                this.uploadedFileName = file.name;
                this.uploadedFileSize = this.formatFileSize(file.size);
                this.$message.success('文件上传成功');
            } catch (error) {
                this.$message.error(`文件上传失败: ${error.message}`);
                console.error('文件上传错误:', error);
            }
        },

        // 上传文件到OSS
        async uploadFile(file) {
            try {
                const loading = this.$loading({
                    lock: true,
                    text: '文件上传中...',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                // 文件验证
                const allowedTypes = ['text/csv', 'text/plain', 'application/vnd.ms-excel'];
                const allowedExtensions = ['.csv', '.txt'];
                const maxSize = 100 * 1024 * 1024; // 100MB

                // 检查文件类型
                const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
                if (!allowedExtensions.includes(fileExtension) && allowedTypes.indexOf(file.type) === -1) {
                    throw new Error(`不支持的文件类型: ${file.type}，仅支持 CSV、TXT 格式`);
                }

                if (file.size > maxSize) {
                    throw new Error(`文件大小超过限制 (100MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
                }

                // 获取上传签名并上传
                const signature = await getUploadSignature('file');
                const res = await uploadToOSS(file, signature);

                loading.close();
                return res.url;
            } catch (error) {
                this.$loading().close();
                console.error('文件上传失败:', error);
                throw error;
            }
        },

        // 文件上传前的验证
        beforeFileUpload(file) {
            const allowedTypes = ['text/csv', 'text/plain', 'application/vnd.ms-excel'];
            const allowedExtensions = ['.csv', '.txt'];
            const maxSize = 100 * 1024 * 1024; // 100MB

            // 检查文件扩展名
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(fileExtension) && allowedTypes.indexOf(file.type) === -1) {
                this.$message.error('只支持 CSV、TXT 格式的文件!');
                return false;
            }

            if (file.size > maxSize) {
                this.$message.error('文件大小不能超过 100MB!');
                return false;
            }

            return true;
        },

        // 删除文件
        removeFile() {
            this.uploadForm.file_url = '';
            this.uploadedFileName = '';
            this.uploadedFileSize = '';
        },

        // 格式化文件大小
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        // 获取同步状态
        async fetchSyncStatus() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'get_sync_status',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                    }
                );

                if (response.data) {
                    this.syncStatus = response.data;
                }
            } catch (error) {
                console.error('获取同步状态失败:', error);
            }
        },

        // 获取统计信息
        async fetchStats() {
            try {
                // 获取人群总数 - 直接从分页数据获取
                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'list_audiences',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                        page: 1,
                        page_size: 1  // 只需要获取总数，不需要实际数据
                    }
                );

                if (response.data.pagination) {
                    this.stats = {
                        total_audiences: response.data.pagination.total || 0
                    };
                } else {
                    this.stats = {
                        total_audiences: 0
                    };
                }

                // 获取文件总数
                const fileResponse = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'list_audience_files',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                        page: 1,
                        page_size: 1
                    }
                );

                if (fileResponse.data.pagination) {
                    this.fileStats = {
                        total_files: fileResponse.data.pagination.total || 0
                    };
                } else {
                    this.fileStats = {
                        total_files: 0
                    };
                }
            } catch (error) {
                console.error('获取统计信息失败:', error);
                // 设置默认值
                this.stats = {
                    total_audiences: 0
                };
                this.fileStats = {
                    total_files: 0
                };
            }
        },
        // 获取授权账户列表
        async fetchAuthList() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/gzh_auth_management.php`,
                    {
                        action: 'list_all',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                    }
                );

                if (response.data.data) {
                    this.authList = response.data.data.filter(item => item.ghz_appid && item.ghz_appid.trim() !== '');
                }
            } catch (error) {
                console.error('获取授权账户列表失败:', error);
            }
        },

        // 授权账户选择变化
        async handleAuthChange(authId) {
            if (authId) {
                this.accountList = [];
                this.audienceForm.account_id = '';
                await this.fetchAccountsByAuth(authId);
            } else {
                this.accountList = [];
                this.audienceForm.account_id = '';
            }
        },

        // 根据授权账户获取广告商
        async fetchAccountsByAuth(authId) {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/advertiser_sync.php`,
                    {
                        action: 'list',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                    }
                );

                if (response.data.data) {
                    // 过滤出当前授权账户下的广告商
                    this.accountList = response.data.data.filter(account =>
                        account.auth_id == authId
                    );
                }
            } catch (error) {
                console.error('获取广告商失败:', error);
            }
        },

        // 计算状态统计
        // 计算状态统计
        calculateStatusCount() {
            this.statusCount = {
                active: 0,
                pending: 0
            };

            this.audienceList.forEach(item => {
                if (item.status === 'SUCCESS') {  // 修改这里：从 NORMAL 改为 SUCCESS
                    this.statusCount.active++;
                } else if (item.status === 'PENDING') {
                    this.statusCount.pending++;
                }
            });
        },

        // 同步人群数据
        async syncAudiences() {
            if (!this.authList.length) {
                this.$message.warning('没有可用的授权账户，请先添加广告授权');
                return;
            }

            try {
                // 获取所有广告商数据
                const accountsResponse = await axios.post(
                    `${config.BASE_URL}/pc/api/advertiser_sync.php`,
                    {
                        action: 'list',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                    }
                );

                if (!accountsResponse.data.data || accountsResponse.data.data.length === 0) {
                    this.$message.warning('请先创建广告商数据');
                    return;
                }

                // 构建选择选项
                this.syncAccountOptions = [];
                this.authList.forEach(auth => {
                    const authAccounts = accountsResponse.data.data.filter(acc =>
                        acc.auth_id == auth.id
                    );
                    authAccounts.forEach(account => {
                        this.syncAccountOptions.push({
                            label: `${auth.ghz_name} - ${account.corporation_name} (${account.account_id})`,
                            value: {
                                auth_id: auth.id,
                                account_id: account.account_id,
                                auth_name: auth.ghz_name,
                                corporation_name: account.corporation_name
                            }
                        });
                    });
                });

                if (this.syncAccountOptions.length === 0) {
                    this.$message.warning('没有找到可用的授权账户和广告商组合');
                    return;
                }

                // 如果有多个选择，显示选择弹窗
                if (this.syncAccountOptions.length > 1) {
                    this.syncSelectionForm.selectedAccount = this.syncAccountOptions[0];
                    this.showSyncSelectionDialog = true;
                } else {
                    // 只有一个账户，直接同步
                    const selected = this.syncAccountOptions[0].value;
                    await this.startSyncAudiences(selected);
                }

            } catch (error) {
                this.$message.error('获取账户数据失败');
                console.error('获取账户数据错误:', error);
            }
        },

        // 确认同步选择
        async confirmSyncSelection() {
            if (!this.syncSelectionForm.selectedAccount) {
                this.$message.warning('请选择同步账户');
                return;
            }

            this.showSyncSelectionDialog = false;
            await this.startSyncAudiences(this.syncSelectionForm.selectedAccount.value);
        },

        // 执行同步操作
        async startSyncAudiences(selected) {
            try {
                await this.$confirm(
                    `确定要同步客户人群数据吗？\n同步账户：${selected.auth_name} - ${selected.corporation_name} (${selected.account_id})`,
                    '同步确认',
                    {
                        confirmButtonText: '开始同步',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }
                );

                this.syncLoading = true;

                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'sync_audiences',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                        auth_id: selected.auth_id,
                        account_id: selected.account_id
                    }
                );

                if (response.data.success) {
                    this.$message.success(response.data.message);
                    this.fetchAudienceList();
                    this.fetchSyncStatus();
                } else {
                    this.$message.error(response.data.error || '同步失败');
                }

            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('同步失败');
                    console.error('同步人群数据错误:', error);
                }
            } finally {
                this.syncLoading = false;
            }
        },
        // 提交人群表单
        async submitAudience() {
            try {
                await this.$refs.audienceFormRef.validate();

                this.submitting = true;

                const formData = {
                    ...this.audienceForm,
                    store_id: this.userInfo.store_id,
                    user_id: this.userInfo.id,  // 添加user_id
                };

                const action = this.editMode ? 'update_audience' : 'create_audience';

                // 如果是编辑模式，确保包含 audience_id
                if (this.editMode && !formData.audience_id) {
                    this.$message.error('缺少人群ID参数');
                    return;
                }

                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: action,
                        ...formData
                    }
                );

                if (response.data.success) {
                    this.$message.success(this.editMode ? '人群更新成功' : '人群创建成功');
                    this.showCreateDialog = false;
                    this.fetchAudienceList();
                    this.fetchSyncStatus();
                    this.resetAudienceForm();
                } else {
                    this.$message.error(response.data.error || '操作失败');
                }
            } catch (error) {
                if (error instanceof Error) {
                    // 验证失败，不显示错误消息
                } else {
                    this.$message.error('操作失败');
                    console.error('提交人群表单错误:', error);
                }
            } finally {
                this.submitting = false;
            }
        },

        // 编辑人群
        editAudience(audience) {
            this.editMode = true;
            this.audienceForm = {
                name: audience.name,
                auth_id: audience.auth_id,
                account_id: audience.account_id,
                type: audience.type,
                outer_audience_id: audience.outer_audience_id || this.generateOuterAudienceId(),
                description: audience.description || '',
                cooperated: Boolean(audience.cooperated),
                audience_id: audience.audience_id
            };

            // 编辑模式下如果有外部ID，则关闭自动生成
            this.autoGenerateOuterId = !audience.outer_audience_id;

            this.currentAudience = { ...audience };
            this.showCreateDialog = true;
        },

        // 修改这个打开上传弹窗的方法名
        openUploadDialog(audience) {
            this.currentAudience = { ...audience };
            this.uploadForm = {
                user_id_type: 'MOBILE_PHONE',
                file_url: '',
                operation_type: 'APPEND',
                open_app_id: ''
            };
            this.showUploadDialog = true;
        },

        // 提交文件上传
        async submitUpload() {
            try {

                // 手动验证微信AppID（如果需要）
                if (this.uploadForm.user_id_type === 'WECHAT_OPENID' && !this.uploadForm.open_app_id) {
                    this.$message.warning('请填写微信AppID');
                    return;
                }
                await this.$refs.uploadFormRef.validate();

                this.uploading = true;

                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'upload_audience_file',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                        auth_id: this.currentAudience.auth_id,
                        account_id: this.currentAudience.account_id,
                        audience_id: this.currentAudience.audience_id,
                        ...this.uploadForm
                    }
                );

                if (response.data.success) {
                    this.$message.success('文件上传成功');
                    this.showUploadDialog = false;
                } else {
                    this.$message.error(response.data.error || '上传失败');
                }
            } catch (error) {
                if (error instanceof Error) {
                    // 验证失败，不显示错误消息
                    if (this.uploadForm.user_id_type === 'WECHAT_OPENID' && !this.uploadForm.open_app_id) {
                        this.$message.warning('选择微信OpenId类型时，微信AppID为必填项');
                    }
                } else {
                    this.$message.error('上传失败');
                    console.error('提交文件上传错误:', error);
                }
            } finally {
                this.uploading = false;
            }
        },
        // 查看文件 - 添加参数控制是否筛选特定人群
        async viewFiles(audience, filterByAudience = true) {
            this.currentAudience = { ...audience };

            if (filterByAudience) {
                // 如果指定要筛选，就设置人群ID
                this.filesFilter.audience_id = audience.audience_id;
            } else {
                // 不筛选，显示所有文件
                this.filesFilter.audience_id = '';
            }

            this.filesPagination.page = 1;
            this.showFilesDialog = true;

            // 在打开弹窗时自动同步该人群的文件状态
            await this.syncFileStatus(audience);
            this.fetchFileList();
        },


        // 添加查看全部文件的方法
        showAllFiles() {
            this.filesFilter.audience_id = '';
            this.filesPagination.page = 1;
            this.fetchFileList();
        },
        // 获取文件列表
        async fetchFileList() {
            this.filesLoading = true;
            try {
                const params = {
                    action: 'list_audience_files',
                    store_id: this.userInfo.store_id,
                    user_id: this.userInfo.id,  // 添加user_id
                    page: this.filesPagination.page,
                    page_size: this.filesPagination.pageSize
                };

                // 只有当有筛选条件时才传递 audience_id
                if (this.filesFilter.audience_id) {
                    params.audience_id = this.filesFilter.audience_id;
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    params
                );

                if (data.data) {
                    this.fileList = data.data;
                    this.filesPagination.total = data.pagination?.total || data.data.length;
                }
            } catch (error) {
                this.$message.error('获取文件列表失败');
                console.error('获取文件列表错误:', error);
            } finally {
                this.filesLoading = false;
            }
        },

        // 删除人群
        async deleteAudience(audience) {
            try {
                await this.$confirm('确定要删除这个客户人群吗？', '确认删除', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                this.deleteLoading = { ...this.deleteLoading, [audience.id]: true };

                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'delete_audience',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                        auth_id: audience.auth_id,
                        account_id: audience.account_id,
                        audience_id: audience.audience_id
                    }
                );

                if (response.data.success) {
                    this.$message.success('人群删除成功');
                    this.fetchAudienceList();
                    this.fetchSyncStatus();
                } else {
                    this.$message.error(response.data.error || '删除失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('删除失败');
                    console.error('删除人群错误:', error);
                }
            } finally {
                this.deleteLoading = { ...this.deleteLoading, [audience.id]: false };
            }
        },

        // 删除本地文件记录
        async deleteLocalFile(file) {
            try {
                await this.$confirm('确定要删除这个文件记录吗？', '确认删除', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                this.deleteFileLoading = { ...this.deleteFileLoading, [file.id]: true };

                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'delete_local_file',
                        store_id: this.userInfo.store_id,
                        user_id: this.userInfo.id,  // 添加user_id
                        id: file.id
                    }
                );

                if (response.data.success) {
                    this.$message.success('文件记录删除成功');
                    this.fetchFileList();
                } else {
                    this.$message.error(response.data.error || '删除失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('删除失败');
                    console.error('删除文件记录错误:', error);
                }
            } finally {
                this.deleteFileLoading = { ...this.deleteFileLoading, [file.id]: false };
            }
        },

        // 搜索
        handleSearch() {
            this.pagination.page = 1;
            this.fetchAudienceList();
        },

        // 重置搜索
        resetSearch() {
            this.searchForm = {
                keyword: '',
                status: ''
            };
            this.pagination.page = 1;
            this.fetchAudienceList();
        },

        // 重置文件筛选
        resetFilesFilter() {
            this.filesFilter.audience_id = '';
            this.filesPagination.page = 1;
            this.fetchFileList();
        },

        // 处理自动生成开关变化
        handleAutoGenerateChange(value) {
            if (value) {
                // 开启自动生成时，生成新的ID
                this.generateNewOuterId();
            } else {
                // 关闭自动生成时，清空ID让用户手动输入
                this.audienceForm.outer_audience_id = '';
            }
        },

        // 生成新的外部ID
        generateNewOuterId() {
            this.audienceForm.outer_audience_id = this.generateOuterAudienceId();
        },

        // 在打开创建弹窗时自动生成ID
        // 重置表单
        resetAudienceForm() {
            this.audienceForm = {
                name: '',
                auth_id: '',
                account_id: '',
                type: 'CUSTOMER_FILE',
                outer_audience_id: this.generateOuterAudienceId(), // 自动生成
                description: '',
                cooperated: false,
                audience_id: ''
            };
            this.autoGenerateOuterId = true;
            this.editMode = false;
            this.currentAudience = {}; // 清空当前操作的人群
            this.$refs.audienceFormRef?.clearValidate();
        },
        // 工具函数
        getTypeTagType(type) {
            const typeMap = {
                'CUSTOMER_FILE': 'primary',
                'LOOKALIKE': 'success',
                'USER_ACTION': 'warning',
                'KEYWORD': 'info',
                'COMBINE': 'danger'
            };
            return typeMap[type] || 'info';
        },

        getTypeText(type) {
            const textMap = {
                'CUSTOMER_FILE': '客户文件',
                'LOOKALIKE': 'Lookalike',
                'USER_ACTION': '用户行为',
                'KEYWORD': '关键词',
                'COMBINE': '组合'
            };
            return textMap[type] || type;
        },

        getStatusTagType(status) {
            const typeMap = {
                'SUCCESS': 'success',
                'PROCESSING': 'warning',    // 处理中 - 橙色
                'PENDING': 'info',          // 等待中 - 蓝色
                'ERROR': 'danger',
                'DELETED': 'danger'
            };
            return typeMap[status] || 'info';
        },

        getStatusText(status) {
            const textMap = {
                'SUCCESS': '活跃',
                'PROCESSING': '处理中',
                'PENDING': '等待中',
                'ERROR': '错误',
                'DELETED': '已删除'
            };
            return textMap[status] || status;
        },

        getUserTypeText(userType) {
            const textMap = {
                'MOBILE_PHONE': '手机号',
                'WX_OPENID': '微信OpenId',
                'QQ_ID': 'QQ号',
                'IDFA': 'IDFA',
                'IMEI': 'IMEI'
            };
            return textMap[userType] || userType;
        },

        // 修改 getProcessStatusType 方法
        getProcessStatusType(status) {
            const typeMap = {
                'PROCESSING': 'warning',  // 处理中 - 橙色
                'PENDING': 'info',        // 等待中 - 蓝色
                'SUCCESS': 'success',     // 成功 - 绿色
                'ERROR': 'danger',
                'FAILED': 'danger'        // 失败 - 红色
            };
            return typeMap[status] || 'info';
        },

        // 修改 getProcessStatusText 方法
        getProcessStatusText(status) {
            const textMap = {
                'PROCESSING': '处理中',
                'PENDING': '等待中',  // 添加这一行
                'SUCCESS': '成功',
                'ERROR': '错误',
                'FAILED': '失败'
            };
            return textMap[status] || status;
        },

        formatTime(time) {
            if (!time) return '-';
            return new Date(time).toLocaleString('zh-CN');
        }
    }
}
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.header-section {
    margin-bottom: 24px;
}

.stats-section {
    margin-bottom: 24px;
}

.stat-card {
    display: flex;
    align-items: center;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

    .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
        font-size: 24px;
        color: white;

        &.total {
            background: linear-gradient(135deg, #409EFF, #67C2EF);
        }

        &.active {
            background: linear-gradient(135deg, #67C23A, #85CE61);
        }

        &.pending {
            background: linear-gradient(135deg, #E6A23C, #EBB563);
        }

        &.files {
            background: linear-gradient(135deg, #909399, #A6A9AD);
        }
    }

    .stat-content {
        flex: 1;

        .stat-number {
            font-size: 24px;
            font-weight: 700;
            color: #303133;
            margin-bottom: 4px;
        }

        .stat-label {
            font-size: 14px;
            color: #909399;
        }
    }
}

.action-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.sync-status {
    display: flex;
    gap: 20px;
    background: #f8fafc;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    margin-left: auto;
}

.status-item {
    display: flex;
    align-items: center;

    .label {
        color: #606266;
        font-weight: 500;
    }

    .value {
        font-weight: 600;
        margin-left: 4px;

        &.success {
            color: #67c23a;
        }

        &.fail {
            color: #f56c6c;
        }
    }
}

.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

.empty-value {
    color: #909399;
    font-style: italic;
}

.action-buttons {
    display: flex;
    gap: 8px;

    .el-button {
        flex: 1;
        min-width: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        .el-icon {
            margin-right: 4px;
        }
    }
}

.files-content {
    .files-filter {
        margin-bottom: 16px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 6px;
    }
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

/* 表格样式优化 */
.el-table {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table__header) th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
    }

    :deep(.el-table__body) tr:hover>td {
        background-color: #f0f7ff !important;
    }
}

/* 响应式设计 */
@media (max-width: 1200px) {
    .settings-form {
        max-width: 100%;
        padding: 20px;
    }

    .action-bar {
        flex-wrap: wrap;
    }

    .sync-status {
        margin-left: 0;
        margin-top: 12px;
    }
}

@media (max-width: 768px) {
    .stats-section .el-col {
        margin-bottom: 16px;
    }

    .search-bar {
        flex-wrap: wrap;
        gap: 10px;
    }

    .action-buttons {
        flex-direction: column;
    }
}

.sync-selection-content {
    padding: 10px 0;
}

/* 选中账户信息卡片样式 */
.sync-selection-content .el-card {
    border: 1px solid #e6e6e6;
    background-color: #f8fafc;
}

.sync-selection-content .el-card p {
    margin: 5px 0;
    font-size: 14px;
    color: #606266;
}

/* 文件上传样式 */
.file-upload-section {
    width: 100%;
}

.file-upload-area {
    border: 2px dashed #dcdfe6;
    border-radius: 6px;
    padding: 20px;
    text-align: center;
    transition: border-color 0.3s;
    background: #fafafa;
}

.file-upload-area:hover {
    border-color: #409EFF;
}

.file-uploader {
    width: 100%;
}

.upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    cursor: pointer;
}

.upload-text {
    text-align: center;
}

.upload-title {
    font-size: 16px;
    color: #409EFF;
    margin: 0 0 4px 0;
    font-weight: 500;
}

.upload-tip {
    font-size: 12px;
    color: #909399;
    margin: 0;
}

/* 文件预览样式 */
.file-preview {
    border: 1px solid #e6e6e6;
    border-radius: 6px;
    padding: 16px;
    background: #f8fafc;
}

.file-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.file-details {
    flex: 1;
}

.file-name {
    font-size: 14px;
    color: #303133;
    margin: 0 0 4px 0;
    font-weight: 500;
}

.file-size {
    font-size: 12px;
    color: #909399;
    margin: 0;
}

.file-actions {
    display: flex;
    gap: 8px;
}

/* 响应式调整 */
@media (max-width: 768px) {
    .file-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .file-actions {
        align-self: flex-end;
    }
}

/* 上传提示样式 */
.upload-tips {
    margin-top: 12px;
    padding: 12px;
    background: #f0f9ff;
    border: 1px solid #e1f5fe;
    border-radius: 4px;
}

.tip-text {
    font-size: 13px;
    color: #606266;
    margin: 0 0 4px 0;
    line-height: 1.5;
}

.tip-note {
    font-size: 12px;
    color: #909399;
    margin: 0;
    font-style: italic;
}

.template-link {
    color: #409EFF;
    text-decoration: none;
    font-weight: 500;
}

.template-link:hover {
    color: #67C23A;
    text-decoration: underline;
}

.outer-id-container {
    display: flex;
    align-items: center;
    width: 100%;

    .el-input {
        flex: 1;
        min-width: 0;
        /* 防止flex布局溢出 */
    }
}

.regenerate-btn {
    margin-left: 12px !important;
    padding: 8px 12px !important;
    height: auto !important;
    border-radius: 4px !important;
    border: 1px solid #409EFF !important;
    background-color: #f0f7ff !important;
    color: #409EFF !important;
    font-size: 13px !important;

    /* 悬停效果 */
    &:hover {
        background-color: #409EFF !important;
        color: white !important;
        border-color: #409EFF !important;
    }

    /* 激活效果 */
    &:active {
        background-color: #0d84ff !important;
        border-color: #0d84ff !important;
    }

    /* 图标间距 */
    .el-icon {
        margin-right: 4px !important;
    }
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 6px;
    line-height: 1.4;
}

/* 添加强制字段的样式 */
.required-field .el-input__inner {
    border-color: #f56c6c;
}

.required-field .el-input__inner:focus {
    border-color: #f56c6c;
    box-shadow: 0 0 0 2px rgba(245, 108, 108, 0.2);
}

/* 微信AppID必填提示样式 */
.form-tip.wechat-required {
    color: #f56c6c;
    font-weight: 500;
}
</style>